import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Row, Col, Dropdown, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as moment from "moment";
import ReactToPrint from "react-to-print";
import ExportToExcel from "../../components/table/ExportToExcel";

/** Elements */
import ModalConfigPaymentReminder from "./modals/ModalConfigPaymentReminder";
import ContentSolid from "../../components/global/ContentSolid";
import { LoadingTable } from "../../components/lazyLoading/LazyLoading";
import ContentTable from "../../components/table/ContentTable";
import { Table } from "../../components/table/Table";
import { thousandsFormat } from "../../components/global/GlobalTools";
import SelectFilter from "../../components/table/SelectFilter";
import InputFilter from "../../components/table/InputFilter";
import Label from "../../components/table/Label";
import DateRangeFilter from "../../components/table/DateRangeFilter";

//API
import { GetOverdueDebts } from "../../api/PaymentsReport";

//Custom styles section
const customStyles = {
  textDangerBold: {
    color: "#ea2c54",
    fontWeight: "bold",
    fontSize: "small",
  },
  textGrayBold: {
    color: "#c8cbcc",
    fontWeight: "bold",
    fontSize: "small",
  },
};

const OverdueDebtsView = () => {
  const [t, i18n] = useTranslation(["reports", "global", "table"]); //In the array that is passed as an argument in useTranslation, the translations are loaded per module
  const componentRef = useRef();
  const [loading, setLoading] = useState(true);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectAllRows, setSelectAllRows] = useState({
    switch: false,
    value: false,
  });
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const [count, setCount] = useState(0);
  const [printExport, setPrintExport] = useState(true);
  //Options to export to excel
  const exportOptions = {
    openAsDownload: true,
    format: "xlsx", //'xlsx' or 'xls' or 'csv'
    filename: t("report"),
  };
  const [exportData, setExportData] = useState([]);
  const [showConfigPaymentReminderModal, setShowConfigPaymentReminderModal] =
    useState(false);
  const [loadingView, setLoadingView] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  // const [values, setValues] = useState([]);

  /**Initial loading */
  useEffect(() => {
    getDebts();
  }, []);

  const badgeStatus = (value, status) => {
    if (value == 0) {
      status = "today";
    }
    const typeLabel = {
      expired: "danger",
      today: "warning",
      no_expired: "warning",
      never_expires: "secondary",
    };
    const textLabel = {
      today: t("expiresToday"),
      no_expired: `${value} ${
        value > 1 ? t("daysRemaining") : t("dayRemaining")
      }`,
      never_expires: t("notApplicable"),
    };
    value = textLabel[status];
    return (
      <Label
        values={`${value}`}
        bg={typeLabel[status]}
        style={{ width: "100%" }}
      />
    );
  };

  let futureOptions = [
    {
      label: t("filterSelect.today"),
      value: 2,
      startDate: moment().startOf("day").unix(),
      endDate: moment().endOf("day").unix(),
    },
    //mañana
    {
      label: t("filterSelect.tomorrow"),
      value: 3,
      startDate: moment().add(1, "day").startOf("day").unix(),
      endDate: moment().add(1, "day").endOf("day").unix(),
    },
    // proximos 7 días
    {
      label: t("filterSelect.nextSevenDays"),
      value: 4,
      startDate: moment().add(1, "day").startOf("day").unix(),
      endDate: moment().add(7, "day").endOf("day").unix(),
    },
    // proximos 30 días
    {
      label: t("filterSelect.nextThirtyDays"),
      value: 5,
      startDate: moment().add(1, "day").startOf("day").unix(),
      endDate: moment().add(30, "day").endOf("day").unix(),
    },
    {
      label: t("filterSelect.selectRange"),
      value: 6,
      startDate: "",
      endDate: "",
    },
  ];

  //Saves the data for the table in memory, and is not updated unless useMemo identifies a change in the data
  const data = React.useMemo(() => dataTable, [loading]);

  //Columns of the table
  const columns = React.useMemo(() => {
    if (!i18n.isInitialized) {
      return [];
    }
    return [
      {
        Header: t("headerTable.internalEnrollment"), //Header text
        accessor: "unique_id", //accessor is the "key" in the data
        minWidth: dataTable.length && count != 0 ? 110 : 130,
        width: 140,
        maxWidth: 150,
      },
      {
        Header: t("headerTable.name"),
        accessor: "student",
        minWidth: 50,
        width: 100,
        maxWidth: 120,
      },
      {
        Header: t("filterSelectSchoolCycle.schoolCycle"),
        accessor: "school_cycle",
        Filter: SelectFilter,
        filter: "equals", // Strict filtering
        minWidth: 50,
        width: 100,
        maxWidth: 120,
      },
      {
        Header: t("headerTable.schoolLevel"),
        accessor: "school_level",
        Filter: SelectFilter, //Filter: InputFilter | SelectFilter | DateRangeFilter (InputFilter is the default filter)
        minWidth: 50,
        width: 100,
        maxWidth: 110,
      },
      {
        Header: t("headerTable.grade"),
        accessor: "grade",
        Filter: SelectFilter,
        minWidth: 20,
        width: 100,
        maxWidth: 120,
      },
      {
        Header: t("headerTable.daysRemaining"),
        accessor: "daysLate",
        Filter: (props) => <InputFilter {...props} />,
        typeElement: "label",
        Cell: ({ cell: { value }, row: { original } }) => {
          return badgeStatus(value, original.expiration_status);
        },
        minWidth: 50,
        width: 120,
        maxWidth: 150,
      },
      {
        Header: t("headerTable.expiration"),
        accessor: "end_date",
        Filter: (props) => (
          <DateRangeFilter
            getByDateRange={getDebts}
            {...props}
            futureDatesSelectables={true} //To allow selecting future dates
            pastDatesSelectable={false} //To allow selecting past dates
            options={futureOptions} //Options for future dates
            extraOptions={[
              {
                label: t("status.allFeminine"),
                value: 10,
                start_date: null,
                end_date: null,
                isDefault: true,
              },
            ]}
          />
        ),
        filter: "dateBetween",
        minWidth: 50,
        width: 120,
        maxWidth: 150,
      },
      {
        Header: t("headerTable.concept"),
        accessor: "concept_name",
        minWidth: 50,
        width: 80,
        maxWidth: 120,
      },
      {
        Header: t("headerTable.subconcept"),
        accessor: "subconcept_name",
        minWidth: 50,
        width: 150,
        maxWidth: 170,
      },
      {
        Header: t("headerTable.charge"),
        accessor: "amountFormat",
        Summation: (info) => {
          // Calculate summation of amount if rows change
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) => parseFloat(row.original.amount) + sum,
                0
              ),
            [info.rows]
          );
          return `$ ${thousandsFormat(total)}`;
        },
        propertySummation: "amount",
        minWidth: 80,
        width: 100,
        maxWidth: 120,
      },
      {
        Header: t("headerTable.surcharge"),
        accessor: "surchargeFormat",
        Summation: (info) => {
          // Calculate summation of surcharge if rows change
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) => parseFloat(row.original.surcharge) + sum,
                0
              ),
            [info.rows]
          );
          return `$ ${thousandsFormat(total)}`;
        },
        propertySummation: "surcharge", //Indicates the property in the "row.original" array to be added
        minWidth: 80,
        width: 150,
        maxWidth: 180,
      },
      {
        Header: t("headerTable.scholarship"),
        accessor: "scholarshipFormat",
        Summation: (info) => {
          // Calculate summation of amount if rows change
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) => parseFloat(row.original.scholarship) + sum,
                0
              ),
            [info.rows]
          );
          return total == 0.0 ? (
            <span style={{ color: "#53B567" }}>$ {thousandsFormat(total)}</span>
          ) : (
            <span style={{ color: "#53B567" }}>
              {" "}
              -$ {thousandsFormat(total)}
            </span>
          );
        },
        propertySummation: "scholarship",
        Cell: ({ cell: { value } }) => {
          return <span style={{ color: "#53B567" }}>{value}</span>;
        },
        minWidth: 80,
        width: 150,
        maxWidth: 180,
      },
      {
        Header: t("headerTable.total"),
        accessor: "totalFormat",
        disableSortBy: true,
        Summation: (info) => {
          // Calculate summation of total if rows change
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) => parseFloat(row.original.total) + sum,
                0
              ),
            [info.rows]
          );
          return `$ ${thousandsFormat(total)}`;
        },
        propertySummation: "total",
        minWidth: 80,
        width: 150,
        maxWidth: 180,
      },
      {
        Header: t("headerTable.previousPaid"),
        accessor: "previousPaidFormat",
        Summation: (info) => {
          // Calculate summation of previous paid if rows change
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) => parseFloat(row.original.previous_paid) + sum,
                0,
              ),
            [info.rows],
          );
          return `$ ${thousandsFormat(total)}`;
        },
        propertySummation: "previous_paid",
        minWidth: 80,
        width: 150,
        maxWidth: 180,
      },
    ];
  }, [i18n.isInitialized]);

  /**
   * Function to fetch overdue debts based on provided dates.
   * @param {*} dates
   */
  const getDebts = (dates = {}) => {
    setSelectedRowIds([]);
    setLoading(true);
    let payload = {
      student_status: "Active", // Status of the student
      //   timezone: moment.tz.guess(), // Timezone of the user
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    // If dates are provided, add them to the payload
    if (dates && dates.start_date && dates.end_date) {
      payload.start_date = dates.start_date || 0; // Start date for filtering
      payload.end_date = dates.end_date || 0; // End date for filtering
    }

    GetOverdueDebts(payload)
      .then((response) => {
        if (response.status) {
          let data = response.data;
          for (let i = 0; i < data.length; i++) {
            const hasExpirationDate = Boolean(data[i].end_date);
            data[i].surchargeFormat = `$ ${thousandsFormat(data[i].surcharge)}`;
            data[i].scholarshipFormat =
              data[i].scholarship == 0.0
                ? ` $ ${thousandsFormat(data[i].scholarship)}`
                : `-$ ${thousandsFormat(data[i].scholarship)}`;
            data[i].amountFormat = `$ ${thousandsFormat(data[i].amount)}`;
            data[i].previousPaidFormat = `$ ${thousandsFormat(
              data[i].previous_paid,
            )}`;
            data[i].totalFormat = `$ ${thousandsFormat(data[i].total)}`;
            data[i].debtFormat = `$ ${thousandsFormat(data[i].debt)}`;

            data[i].index = data[i].id;
            data[i].daysLate = data[i].days_remaining;
            data[i].end_date = hasExpirationDate
              ? moment.utc(data[i].end_date).local().format("DD/MM/YYYY")
              : t("status.withoutDate");
            data[i].restriction_type = data[i].restriction_type || "-";
          }

          setDataTable(response.data);
          setCount(response.data.length);
          setLoadingView(false);
          setLoading(false);
        } else {
          setDataTable([]);
          setCount(0);
          setLoadingView(false);
          setLoading(false);
        }
      })
      .finally(() => {
        // Reset loading state after fetching data
        setLoading(false);
        setLoadingView(false);
      });
  };

  return (
    <div>
      <Row className="mbottom20">
        <Col xs={12} lg={12} style={{ position: "relative" }}>
          <Button
            className="me-2"
            variant="outline-secondary"
            onClick={() => {
              let selectAll = {
                switch: !selectAllRows.switch,
                value: !selectAllRows.value,
              };
              setSelectAllRows(selectAll);
            }}
          >
            {!isAllRowsSelected
              ? t("global:buttons.selectAll")
              : t("global:buttons.unselectAll")}
          </Button>
          {/* Total counter of records and selected records in Debit Report table */}
          {selectedRowIds.length ? (
            selectedRowIds.length == 1 ? (
              <p className="d-inline" style={customStyles.textDangerBold}>
                {t("table:textTableViews.selectedRecord", {
                  countSelected: selectedRowIds.length,
                })}
              </p>
            ) : (
              <p className="d-inline" style={customStyles.textDangerBold}>
                {t("table:textTableViews.selectedRecords", {
                  countSelected: selectedRowIds.length,
                })}
              </p>
            )
          ) : (
            <p className="d-inline" style={customStyles.textGrayBold}>
              {t("table:textTableViews.totalRecords", {
                count: count,
              })}
            </p>
          )}
          <div className="float-end">
            <Dropdown className="d-md-inline-block me-2">
              <Dropdown.Toggle
                id="dropdown-button-dark-example1"
                variant="outline-secondary"
                disabled={printExport}
              >
                <i className="bi bi-download"></i>
                {t("global:buttons.download")}
              </Dropdown.Toggle>
              <Dropdown.Menu disabled={printExport}>
                <ReactToPrint
                  trigger={() => (
                    <Dropdown.Item>{t("global:buttons.print")}</Dropdown.Item>
                  )}
                  content={() => componentRef.current}
                />
                <ExportToExcel
                  exportData={exportData}
                  exportOptions={exportOptions}
                  printExport={printExport}
                  typeElement="dropdownItem"
                />
              </Dropdown.Menu>
            </Dropdown>
            <div className="d-md-inline-block">
              <Button
                className="me-2"
                variant="outline-secondary"
                onClick={() => setShowConfigPaymentReminderModal(true)}
              >
                <i className="bi bi-bell" />
                {t("reports:reminder.paymentReminders")}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {loadingView ? (
        <ContentSolid style={{ marginTop: "20px" }}>
          <LoadingTable />
        </ContentSolid>
      ) : (
        <>
          <ContentTable startColumFilter={1} lastColumFilter={7}>
            <Table
              ref={componentRef}
              columns={columns}
              data={data || []}
              loading={loading}
              rowSelect={true}
              summation={true} //Indicates if there will be a row of sums
              setExportData={setExportData}
              setPrintExport={setPrintExport}
              hiddenColumns={[]} //Indicates the columns that will be hidden by default (The array contains the "accessors" of the columns)
              //   showEditColumns={showEditColumns} //Flag for the modal "Edit List Columns"
              //   setShowEditColumns={setShowEditColumns} //Toggle function of the "Edit List Columns" modal
              setSelectedRowIds={setSelectedRowIds}
              // setValues={setValues}
              //   rowOnclick={showAccountStatusModal} //Function that is executed when clicking on a row
              selectAllRows={selectAllRows}
              setIsAllRowsSelected={setIsAllRowsSelected}
              setRowsDisplayed={setCount}
              limitRowsShown={50} //Limits the rows displayed per page
            />
          </ContentTable>
        </>
      )}

      {/* Additional content can be added here */}
      {showConfigPaymentReminderModal && (
        <ModalConfigPaymentReminder
          showConfigureReminder={showConfigPaymentReminderModal}
          closeModal={() => setShowConfigPaymentReminderModal(false)}
        />
      )}
    </div>
  );
};
export default OverdueDebtsView;
OverdueDebtsView.propTypes = {
  cell: PropTypes.object,
  row: PropTypes.object,
};
